import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import noop from 'lodash/noop';
import { getNotificationSummary } from '../../helpers';
import { DAY_OF_WEEK_LIST, HOURS_OPTIONS } from '../common/constants';
import SelectField from '../common/SelectField';
import IndeterminateCheckbox from '../common/IndeterminateCheckbox';
import Spinner from '@dealroadshow/uikit-loader/Spinner';
import { IFileUploadNotificationSettingsPayload } from '../../domain/vo/FileUploadEmailAlerts';
import {
  IStagingFileUploadNotificationSettingsPayload,
} from '../../domain/vo/StagingFileUploadEmailAlerts';
import { IAccessRequestNotificationSettingsPayload } from '../../domain/vo/AccessRequestEmailAlerts';
import { ITimezone } from '@framework/timezone/vo/Timezone';
import styles from './alerts.module.scss';

interface IProps<T> {
  name: string,
  initialValues: T,
  isFetching?: boolean,
  isDisabled?: boolean,
  timezone?: ITimezone,
  onChange: (value: T) => void,
  isNoneCheckboxVisible?: boolean,
}

const Alerts = <T extends IFileUploadNotificationSettingsPayload |
  IStagingFileUploadNotificationSettingsPayload |
  IAccessRequestNotificationSettingsPayload,
>({
  name = 'dataroomAlertsForm',
  initialValues,
  isFetching = false,
  isDisabled,
  timezone,
  onChange = noop,
  isNoneCheckboxVisible = true,
}: IProps<T>) => {
  const [alertsState, setAlertsState] = useState<T>(initialValues);

  useEffect(() => {
    onChange(alertsState);
  }, [alertsState]);

  useEffect(() => {
    isDisabled
      ? setAlertsState({
        ...initialValues,
        isActiveAlerts: true,
        isDaily: false,
      })
      : setAlertsState(initialValues);
  }, [initialValues, isDisabled]);

  const handleCheckboxFieldChange = (
    field: keyof T & `is${ string }`,
    value: boolean,
  ) => {
    const state: T = {
      ...alertsState,
      [field]: value,
    };

    if (field === 'isActiveAlerts' && value === true) {
      state.isRealTime = false;
      state.isDaily = false;
      state.isWeekly = false;
    } else if (value === true) {
      state.isActiveAlerts = false;
    }

    if (
      state.isActiveAlerts === false &&
      state.isWeekly === false &&
      state.isDaily === false &&
      state.isRealTime === false
    ) {
      state.isActiveAlerts = true;
    }

    setAlertsState(state);
  };

  const handleSelectFieldChange = (
    name: keyof Omit<T, keyof T & `is${ string }`>,
    value: T[keyof Omit<T, keyof T & `is${ string }`>],
  ) => {
    if (!value) return;

    setAlertsState({
      ...alertsState,
      [name]: value,
    });
  };

  return (
    <div data-test={ name }>
      { isNoneCheckboxVisible && (
        <div className={ styles.itemAlerts }>
          <IndeterminateCheckbox
            name="isActiveAlerts"
            dataTest="noneCheckbox"
            label="None"
            disabled={ isDisabled }
            initialValue={ initialValues.isActiveAlerts }
            value={ alertsState.isActiveAlerts }
            onChange={ (value) => handleCheckboxFieldChange('isActiveAlerts' as keyof T & `is${ string }`, value) }
            className={ styles.checkbox }
          />
        </div>
      ) }
      <div className={ styles.itemAlerts }>
        <IndeterminateCheckbox
          name="isRealTime"
          dataTest="realtimeCheckbox"
          label="Real-time"
          disabled={ isDisabled }
          initialValue={ initialValues.isRealTime }
          value={ alertsState.isRealTime }
          onChange={ (value) => handleCheckboxFieldChange('isRealTime' as keyof T & `is${ string }`, value) }
          className={ styles.checkbox }
        />
      </div>
      <div className={ styles.itemAlerts }>
        <IndeterminateCheckbox
          name="isDaily"
          dataTest="dailyCheckbox"
          label="Daily summary"
          disabled={ isDisabled }
          initialValue={ initialValues.isDaily }
          value={ alertsState.isDaily }
          onChange={ (value) => handleCheckboxFieldChange('isDaily' as keyof T & `is${ string }`, value) }
          className={ styles.checkbox }
        />
        <div className={ cn(styles.inlineBlock, styles.wrpSelect) }>
          <SelectField<T>
            name="dailyTime"
            dataTest="dailyTimeAlertSelect"
            selectClassName={ styles.timeSelect }
            options={ HOURS_OPTIONS }
            disabled={ !alertsState.isDaily || !!isDisabled }
            value={ alertsState.dailyTime }
            onChange={ handleSelectFieldChange as any }
          />
        </div>
      </div>
      <div className={ styles.itemAlerts }>
        <IndeterminateCheckbox
          name="isWeekly"
          dataTest="weeklyCheckbox"
          label="Weekly summary"
          disabled={ isDisabled }
          initialValue={ initialValues.isWeekly }
          value={ alertsState.isWeekly }
          onChange={ (value) => handleCheckboxFieldChange('isWeekly' as keyof T & `is${ string }`, value) }
          className={ styles.checkbox }
        />
        <div className={ cn(styles.inlineBlock, styles.wrpSelect) }>
          <SelectField<T>
            name="weeklyTime"
            dataTest="weeklyTimeAlertSelect"
            selectClassName={ styles.timeSelect }
            options={ HOURS_OPTIONS }
            disabled={ !alertsState.isWeekly || !!isDisabled }
            value={ alertsState.weeklyTime }
            onChange={ handleSelectFieldChange as any }
          />
        </div>
        <div className={ cn(
          styles.inlineBlock,
          styles.dayWeek,
        ) }
        >
          <SelectField<T>
            name="weeklyDay"
            dataTest="weeklyDayAlertSelect"
            selectClassName={ styles.selectDaysOfWeek }
            options={ DAY_OF_WEEK_LIST }
            disabled={ !alertsState.isWeekly || !!isDisabled }
            value={ alertsState.weeklyDay }
            onChange={ handleSelectFieldChange as any }
          />
        </div>
      </div>
      { timezone && (
        <div className={ styles.footer }>
          <span className={ styles.strong }>
            Summary:
          </span> { getNotificationSummary(alertsState, timezone.abbr) }
        </div>
      ) }
      <Spinner
        overlay
        isVisible={ isFetching }
      />
    </div>
  );
};

export default Alerts;
